import CodeField from "@/components/form/CodeField";
import CheckboxField from "@/components/form/CheckboxField";
import EntityRecordSelect from "@/components/form/EntityRecordSelect";
import EntitySelect from "@/components/form/EntitySelect";
import GeoField from "@/components/form/GeoField";
import InputField from "@/components/form/InputField";
import RichTextField from "@/components/form/RichTextField";
import SelectField from "@/components/form/SelectField";
import SelectUserField from "@/components/form/SelectUserField";
import RelationshipCollectionField from "@/components/form/RelationshipCollectionField";
import { useEntities } from "@/hooks/useCommonQueries";
import { anyService } from "@/services/breeze/AnyService";
import { AnyValue, CreateUpdateAny, IAny } from "@/types/breeze/Any";
import { IEntity } from "@/types/breeze/Entity";
import {
  FileFieldDatabaseType,
  FileFieldDisplayType,
  Field,
  RelationshipFieldDatabaseType,
  RelationshipFieldDisplayType,
  StandardFieldDatabaseType,
  StandardFieldDisplayType,
  UserFieldDisplayType,
} from "@/types/breeze/Field";
import { formatColumnName } from "@/utils/text";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, Formik, FormikProps } from "formik";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import * as Yup from "yup";
import InputFileField from "@/components/form/InputFileField";
import { Grid, Box } from "@radix-ui/themes";
import { useAuth } from "@/providers/AuthProvider";
import { useEntityData } from "@/hooks/useEntityData";

type EntityFormMode = "view" | "edit" | "create";

export type EntityFormHandle = {
  saveChanges: (cb?: () => void) => void;
  cancelChanges: (cb?: () => void) => void;
};

interface EntityFormProps {
  entity: IEntity;
  initialData?: IAny;
  mode?: EntityFormMode;
  userHasUpdatePermission: boolean;
  disabledFields?: string[];
  splitFieldsIntoGrid?: boolean;
  onCreateSuccess?: (createdRecord: IAny) => void;
  onLoadingStateChange?: (isLoading: boolean) => void;
}

const getValidationSchema = (entity: IEntity) => {
  const shape = entity.fields
    .filter(
      (f) =>
        f.name !== "id" && f.name !== "created_at" && f.name !== "updated_at",
    )
    .reduce((acc: Record<string, Yup.Schema>, field) => {
      // Skip one-to-many and many-to-many relationship fields
      if (
        field.database_type === RelationshipFieldDatabaseType.ONE_TO_MANY ||
        field.database_type === RelationshipFieldDatabaseType.MANY_TO_MANY
      ) {
        return acc;
      }

      // Check for duplicate field names
      if (acc[field.name]) {
        console.warn(`Duplicate field name found: ${field.name}`, field);
      }

      // Initialize with a default validator
      let validator: Yup.Schema = Yup.mixed();

      switch (field.database_type) {
        case StandardFieldDatabaseType.INTEGER:
          validator = Yup.number()
            .nullable()
            .transform((value) => (isNaN(value) ? null : value))
            .min(-2147483648, "Value too small for integer")
            .max(2147483647, "Value too large for integer");
          break;
        case StandardFieldDatabaseType.BIGINTEGER:
          validator = Yup.number()
            .nullable()
            .transform((value) => (isNaN(value) ? null : value))
            .min(Number.MIN_SAFE_INTEGER, "Value too small for bigint")
            .max(Number.MAX_SAFE_INTEGER, "Value too large for bigint");
          break;
        case StandardFieldDatabaseType.DECIMAL:
          validator = Yup.number()
            .nullable()
            .transform((value) => (isNaN(value) ? null : value));
          break;
        case StandardFieldDatabaseType.VARCHAR:
          if (field.name.includes("email")) {
            validator = Yup.string().nullable().email("Invalid email");
          } else {
            validator = Yup.string().nullable();
          }
          break;
        case StandardFieldDatabaseType.BOOLEAN:
          validator = Yup.boolean().nullable();
          break;
        case FileFieldDatabaseType.FILE:
          validator = Yup.array().nullable();
          break;
        default:
          validator = Yup.mixed().nullable();
      }

      if (field.is_required) {
        validator = validator.required("This field is required");
      }

      acc[field.name] = validator;
      return acc;
    }, {});

  return Yup.object().shape(shape);
};

const EntityFormV2 = forwardRef<EntityFormHandle, EntityFormProps>(
  (
    {
      entity,
      initialData,
      mode,
      userHasUpdatePermission,
      disabledFields = [],
      splitFieldsIntoGrid = true,
      onCreateSuccess,
      onLoadingStateChange,
    },
    ref,
  ) => {
    const formRef = useRef<FormikProps<IAny>>(null);

    useImperativeHandle(ref, () => ({
      saveChanges: (cb?: () => void) => {
        formRef.current?.submitForm().then(() => {
          cb?.();
        });
      },
      cancelChanges: (cb?: () => void) => {
        formRef.current?.resetForm();
        cb?.();
      },
    }));

    const queryClient = useQueryClient();
    const { currentTenant, safeNavigate } = useAuth();

    const { data: entities } = useEntities();
    const { invalidateQueryKey } = useEntityData(entity.name);

    const [standardFields, setStandardFields] = useState<Field[]>([]);
    const [fancyFields, setFancyFields] = useState<Field[]>([]);

    const initialValues = useMemo(() => {
      // For create mode, initialize all fields with appropriate default values
      if (mode === "create") {
        return entity.fields.reduce((acc: IAny, field) => {
          // Skip system fields
          if (
            field.name === "id" ||
            field.name === "created_at" ||
            field.name === "updated_at"
          ) {
            return acc;
          }

          // Set default values based on field type
          switch (field.database_type) {
            case StandardFieldDatabaseType.BOOLEAN:
              acc[field.name] = false;
              break;
            case FileFieldDatabaseType.FILE:
              acc[field.name] = [];
              break;
            case StandardFieldDatabaseType.INTEGER:
            case StandardFieldDatabaseType.BIGINTEGER:
            case StandardFieldDatabaseType.DECIMAL:
              acc[field.name] = "";
              break;
            case RelationshipFieldDatabaseType.MANY_TO_ONE:
            case RelationshipFieldDatabaseType.ONE_TO_ONE:
              // Don't set relationship fields in create mode - leave them undefined
              // The relationship will be established after successful creation
              break;
            case RelationshipFieldDatabaseType.MANY_TO_MANY:
              acc[field.name] = [];
              break;
            default:
              // For text fields, etc.
              acc[field.name] = "";
          }
          return acc;
        }, {} as IAny);
      }

      // For edit/view mode, process the existing initialData
      return Object.keys(initialData || {}).reduce((acc: IAny, key) => {
        if (initialData?.[key] !== undefined && initialData?.[key] !== null) {
          // Check if this is a __data object (file field format)
          if (
            typeof initialData[key] === "object" &&
            !Array.isArray(initialData[key]) &&
            initialData[key] !== null &&
            "__data" in (initialData[key] as Record<string, unknown>)
          ) {
            // Pass __data objects through directly for file fields
            acc[key] = initialData[key];
          } else if (
            typeof initialData[key] === "object" &&
            !Array.isArray(initialData[key])
          ) {
            acc[key] = (initialData[key] as IAny).id;
          } else if (Array.isArray(initialData![key])) {
            acc[key] = JSON.stringify(initialData![key]);
          } else {
            acc[key] = initialData?.[key];
          }
        }
        return acc;
      }, {} as IAny);
    }, [mode, entity.fields, initialData]);

    useEffect(() => {
      const newStandardFields: Field[] = [];
      const newFancyFields: Field[] = [];

      // Sort fields by sort_order from the entity definition
      const sortedEntityFields = [...entity.fields].sort(
        (a, b) => a.sort_order - b.sort_order,
      );

      sortedEntityFields.forEach((field) => {
        // Skip system fields when creating
        if (
          mode === "create" &&
          (field.name === "id" ||
            field.name === "created_at" ||
            field.name === "updated_at")
        ) {
          return;
        }

        switch (field.display_type) {
          case FileFieldDisplayType.FILE:
          case StandardFieldDisplayType.GEO:
          case RelationshipFieldDisplayType.RELATIONSHIP:
            newFancyFields.push(field);
            break;
          default:
            newStandardFields.push(field);
            break;
        }
      });

      setStandardFields(newStandardFields); // newStandardFields are now pre-sorted
      setFancyFields(newFancyFields);
    }, [entity, mode]);

    // Group standard fields into rows for rendering
    const standardFieldRows = useMemo(() => {
      const rows: Field[][] = [];
      if (!standardFields) return rows;

      let i = 0;
      while (i < standardFields.length) {
        const field = standardFields[i];
        if (!field) {
          i++;
          continue;
        }

        if (field.width === 100 || !field.width) {
          // Full width field gets its own row
          rows.push([field]);
          i++;
        } else if (field.width === 50) {
          // Half width field - check if next field is also half width
          const nextField = standardFields[i + 1];
          if (nextField && nextField.width === 50) {
            // Two half-width fields in one row
            rows.push([field, nextField]);
            i += 2;
          } else {
            // Single half-width field in row
            rows.push([field]);
            i++;
          }
        } else {
          // Fallback for unknown width, treat as full
          rows.push([field]);
          i++;
        }
      }
      return rows;
    }, [standardFields]);

    // Create mutation for creating a new entity
    const createMutation = useMutation({
      mutationFn: (data: CreateUpdateAny) =>
        anyService.createAny(entity.name, data),
      onSuccess: (createdRecord) => {
        // Invalidate relevant queries to refetch data
        invalidateQueryKey();

        if (onCreateSuccess) {
          // Use custom success handler if provided
          onCreateSuccess(createdRecord);
        } else {
          // Default behavior: navigate back to the entity list
          safeNavigate(`/org/${currentTenant?.id}/entities/${entity.name}`);
        }
      },
      onError: (error: unknown) => {
        console.error("Failed to create:", error);
      },
    });

    // Update mutation for updating an existing entity
    const updateMutation = useMutation({
      mutationFn: ({ id, data }: { id: number; data: CreateUpdateAny }) =>
        anyService.updateAny(entity.name, id, data),
      onSuccess: () => {
        // Invalidate relevant queries to refetch data
        invalidateQueryKey();
      },
      onError: (error: unknown) => {
        console.error("Failed to update:", error);
      },
    });

    // Notify parent of loading state changes
    const isLoading = createMutation.isPending || updateMutation.isPending;
    useEffect(() => {
      onLoadingStateChange?.(isLoading);
    }, [isLoading, onLoadingStateChange]);

    const handleSubmit = async (values: IAny) => {
      try {
        if (mode === "edit") {
          updateMutation.mutate({
            id: initialData!.id,
            data: values as CreateUpdateAny,
          });
        } else {
          // Create new entity
          createMutation.mutate(values as CreateUpdateAny);
        }
      } catch (error) {
        console.error(
          `Failed to ${mode === "edit" ? "update" : "create"}:`,
          error,
        );
      }
    };

    const handleFieldEditSaved = (name: string, value: AnyValue) => {
      updateMutation.mutate({
        id: initialData!.id,
        data: {
          [name]: value,
        },
      });
    };

    const getFieldLabel = (field: Field) => {
      if (field.label) {
        return field.label;
      }
      return formatColumnName(field.name);
    };

    const renderField = (field: Field) => {
      const isFieldDisabled = () => {
        return disabledFields.includes(field.name);
      };

      const isIndividualEditAllowed = () => {
        return (
          userHasUpdatePermission && !isFieldDisabled() && !field.is_immutable
        );
      };

      const isFieldEditing = () => {
        if (!mode) return false;

        // System fields are never editable
        if (
          field.name === "id" ||
          field.name === "created_at" ||
          field.name === "updated_at"
        ) {
          return false;
        }

        if (isFieldDisabled()) {
          return false;
        }

        // Field is editable if:
        // 1. In create mode (regardless of immutability)
        // 2. In edit mode and field is not immutable
        return mode === "create" || (mode !== "view" && !field.is_immutable);
      };

      const standardFieldProps = {
        name: field.name,
        label: getFieldLabel(field),
        description: field.description,
        required: field.is_required,
        isEditing: isFieldEditing(),
        individualEditAllowed: isIndividualEditAllowed(),
        disabled: isFieldDisabled(),
        onEditSaved: handleFieldEditSaved,
      };

      switch (field.display_type) {
        case StandardFieldDisplayType.INPUT:
          return (
            <InputField
              type={
                field.database_type === StandardFieldDatabaseType.INTEGER ||
                field.database_type === StandardFieldDatabaseType.BIGINTEGER ||
                field.database_type === StandardFieldDatabaseType.DECIMAL
                  ? "number"
                  : "text"
              }
              step={
                field.database_type === StandardFieldDatabaseType.DECIMAL
                  ? "any"
                  : field.database_type === StandardFieldDatabaseType.INTEGER ||
                      field.database_type ===
                        StandardFieldDatabaseType.BIGINTEGER
                    ? "1"
                    : undefined
              }
              options={field.options?.input}
              {...standardFieldProps}
            />
          );
        case StandardFieldDisplayType.SELECT:
          return (
            <SelectField
              type={
                field.database_type === StandardFieldDatabaseType.VARCHAR ||
                field.database_type === StandardFieldDatabaseType.TEXT
                  ? "string"
                  : "number"
              }
              options={field.options?.select}
              {...standardFieldProps}
            >
              {field.options?.select?.options?.map((option) => (
                <SelectField.Item key={option.value} value={option.value}>
                  {option.label}
                </SelectField.Item>
              ))}
            </SelectField>
          );
        case StandardFieldDisplayType.ENTITY_SELECT:
          return (
            <EntitySelect
              valuePropName={
                field.database_type === StandardFieldDatabaseType.BIGINTEGER
                  ? "id"
                  : "name"
              }
              {...standardFieldProps}
            />
          );
        case StandardFieldDisplayType.TEXTAREA:
          return (
            <InputField
              as="textarea"
              options={field.options?.textarea}
              {...standardFieldProps}
            />
          );
        case StandardFieldDisplayType.RICH_TEXT:
          return (
            <RichTextField
              options={field.options?.rich_text}
              {...standardFieldProps}
            />
          );
        case StandardFieldDisplayType.CHECKBOX:
          return <CheckboxField {...standardFieldProps} />;
        case StandardFieldDisplayType.SHORT_DATE:
        case StandardFieldDisplayType.LONG_DATE:
          return (
            <InputField
              type="date"
              options={field.options?.input}
              {...standardFieldProps}
            />
          );
        case StandardFieldDisplayType.TIMESTAMP:
          return (
            <InputField
              type="datetime-local"
              options={field.options?.input}
              {...standardFieldProps}
            />
          );
        case StandardFieldDisplayType.JSON:
          return (
            <CodeField language="application/json" {...standardFieldProps} />
          );
        case StandardFieldDisplayType.GEO:
          return <GeoField {...standardFieldProps} />;
        case RelationshipFieldDisplayType.RELATIONSHIP:
          if (
            field.database_type === RelationshipFieldDatabaseType.MANY_TO_ONE ||
            field.database_type === RelationshipFieldDatabaseType.ONE_TO_ONE
          ) {
            const relationship = entity.source_relationships.find(
              (r) => r.id === field.relationship_id,
            );

            if (!relationship) {
              return <p>No relationship found</p>;
            }

            const targetEntity = entities?.find(
              (e) => e.id === relationship.target_entity_id,
            );

            if (!targetEntity) {
              return <p>No target entity found</p>;
            }

            return (
              <EntityRecordSelect
                entity={targetEntity}
                {...standardFieldProps}
                parentRecordId={initialData?.id}
                parentEntityName={entity.name}
              />
            );
          }

          // Handle one-to-many and many-to-many relationships
          if (
            field.database_type === RelationshipFieldDatabaseType.ONE_TO_MANY ||
            field.database_type === RelationshipFieldDatabaseType.MANY_TO_MANY
          ) {
            return (
              <RelationshipCollectionField
                field={field}
                entity={entity}
                initialData={initialData}
                label={getFieldLabel(field)}
                userHasUpdatePermission={userHasUpdatePermission}
                onRelationshipUpdated={() => {
                  // Refresh the related data
                  queryClient.invalidateQueries({
                    queryKey: ["entities", entity.name],
                  });
                  queryClient.invalidateQueries({
                    queryKey: ["entity-data", entity.name, initialData?.id],
                  });
                }}
              />
            );
          }
          return <></>;
        case FileFieldDisplayType.FILE:
          return (
            <InputFileField
              entityId={entity.id}
              fieldDefinition={field}
              {...standardFieldProps}
              options={
                field.options?.file || {
                  max_size: 10485760,
                  max_files: 1,
                  accept: "*/*",
                  public: false,
                }
              }
              multiple
            />
          );
        case UserFieldDisplayType.USER:
          return <SelectUserField {...standardFieldProps} />;
        case "secret":
          return (
            <InputField
              {...standardFieldProps}
              type="password"
              options={{ placeholder: "Enter secret value (encrypted at rest)" }}
            />
          );
        default:
          return <InputField {...standardFieldProps} />;
      }
    };

    return (
      <Box className="pt-4">
        <Formik
          initialValues={initialValues}
          validationSchema={getValidationSchema(entity)}
          onSubmit={handleSubmit}
          innerRef={formRef}
        >
          <Form className="space-y-6">
            {/* Main responsive grid container */}
            <Grid
              columns={{
                initial: "1",
                sm: "1",
                md: "1",
                lg: splitFieldsIntoGrid ? "2" : "1",
              }}
              gap={{ initial: "4", sm: "4", md: "5", lg: "6" }}
              width="auto"
            >
              {/* Standard fields column */}
              <Box>
                {standardFieldRows.map((row, rowIndex) => (
                  <Box key={`std-row-${rowIndex}`} mb="4">
                    {row.length === 2 ? (
                      // Two fields in a row - use responsive grid
                      <Grid
                        columns={{ initial: "1", sm: "2" }}
                        gap={{ initial: "3", sm: "4" }}
                        width="auto"
                      >
                        {row.map((field) => (
                          <Box key={field.name}>{renderField(field)}</Box>
                        ))}
                      </Grid>
                    ) : (
                      // Single field - full width
                      <Box>
                        {row.map((field) => (
                          <Box key={field.name}>{renderField(field)}</Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>

              {/* Fancy fields column */}
              <Box>
                {fancyFields.map((field) => (
                  <Box key={field.name} mb="4">
                    {renderField(field)}
                  </Box>
                ))}
              </Box>
            </Grid>
          </Form>
        </Formik>
      </Box>
    );
  },
);

EntityFormV2.displayName = "EntityFormV2";

export default EntityFormV2;
